import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import SellerLayout from './SellerLayout';
import { useSellerAuth } from '../context/SellerAuthContext';

const SellerDashboard = () => {
  const [loading, setLoading] = useState(true);

  const [data, setData] = useState({
    shop_id: null,
    shop_name: '',
    today: '',
    total_orders: 0,
    today_orders: 0,
    payment_completed: 0,
    delivery_in_progress: 0,
    delivery_done: 0,
    delivery_missing: 0,
    recent_orders: [],
    low_stock_products: [],
    recent_reviews: [],
    recent_qna: [],
  });

  const wsRef = useRef(null);
  const { seller, logout } = useSellerAuth();

  const go = (url) => {
    window.location.href = url;
  };

  const fetchDashboard = async () => {
    try {
      const res = await axios.get('/api/seller/dashboard-data', {
        withCredentials: true,
      });

      if (res.data && res.data.ok) {
        setData({
          shop_id: res.data.shop_id,
          shop_name: res.data.shop_name || '',
          today: res.data.today || '',
          total_orders: res.data.total_orders || 0,
          today_orders: res.data.today_orders || 0,
          payment_completed: res.data.payment_completed || 0,
          delivery_in_progress: res.data.delivery_in_progress || 0,
          delivery_done: res.data.delivery_done || 0,
          delivery_missing: res.data.delivery_missing || 0,
          recent_orders: Array.isArray(res.data.recent_orders) ? res.data.recent_orders : [],
          low_stock_products: Array.isArray(res.data.low_stock_products) ? res.data.low_stock_products : [],
          recent_reviews: Array.isArray(res.data.recent_reviews) ? res.data.recent_reviews : [],
          recent_qna: Array.isArray(res.data.recent_qna) ? res.data.recent_qna : [],
        });
      } else {
        alert('대시보드 데이터를 불러오지 못했습니다.');
      }
    } catch (err) {
      const redirect = err?.response?.data?.redirect;
      if (redirect) {
        window.location.href = redirect;
        return;
      }

      console.error('대시보드 로드 실패:', err);
      alert('대시보드 데이터를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  useEffect(() => {
    const shopId = parseInt(seller?.shop_id || data.shop_id || 0, 10);
    if (!shopId) return;

    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${window.location.host}/ws/?shop_id=${shopId}`);

    wsRef.current = ws;

    ws.onopen = () => {
      console.log('Dashboard WS 연결 성공');
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);

        if (msg.type === 'NEW_ORDER') {
          console.log('Dashboard 새 주문 감지', msg.order_id);
          fetchDashboard();
        }

        if (msg.type === 'ORDER_STATUS_CHANGED') {
          console.log('Dashboard 주문 상태 변경 감지');
          fetchDashboard();
        }
      } catch (e) {
        console.error('Dashboard WS 메시지 처리 실패:', e);
      }
    };

    ws.onerror = (e) => {
      console.log('Dashboard WS 오류', e);
    };

    ws.onclose = (e) => {
      console.log('Dashboard WS 종료', e.code);
    };

    return () => {
      try {
        ws.close();
      } catch (e) {}
    };
  }, [seller?.shop_id, data.shop_id]);

  const formatNumber = (value) => {
    return Number(value || 0).toLocaleString('ko-KR');
  };

  const truncateText = (text, max) => {
    const str = text || '';
    return str.length > max ? `${str.slice(0, max)}...` : str;
  };

  const makeImageSrc = (path) => {
    if (!path || String(path).trim() === '') {
      return '/static/default_thumb.jpg';
    }
  
    const str = String(path);
  
    if (str.startsWith('http')) {
      return str;
    }
  
    return `/static/${str.replace(/^\/+/, '')}`;
  };

  const formatQnaDate = (value) => {
    if (!value) return '';

    if (typeof value === 'string') {
      return value.length >= 10 ? value.slice(5, 10) : value;
    }

    try {
      const d = new Date(value);
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${mm}-${dd}`;
    } catch (e) {
      return '';
    }
  };

  const renderStars = (rating) => {
    return '★'.repeat(Number(rating || 0));
  };

  if (loading) {
    return (
      <div style={styles.loadingWrap}>
        <div style={styles.loadingText}>로딩중...</div>
      </div>
    );
  }

  return (
    <SellerLayout shopName={data.shop_name}>
      <style>{responsiveCss}</style>

      <div style={styles.page}>
        <div style={styles.heroCard}>
          <h4 style={styles.heroTitle}>셀러 대시보드</h4>
          <p style={styles.heroDesc}>
            Today: {data.today} | 상점의 실시간 현황입니다.
          </p>
        </div>

        <div className="seller-content-row" style={styles.contentRow}>
          <div style={styles.column}>
            <div style={styles.card}>
              <h5 style={styles.sectionTitle}>
                <span style={styles.sectionBar}></span>
                Store Summary
              </h5>

              <div style={styles.kpiGrid}>
                <div className="seller-kpi-col" style={styles.kpiCol}>
                  <div
                    style={styles.kpiBoxClickable}
                    onClick={() => go('/seller/kiosk/orders?status=ALL')}
                  >
                    <span style={styles.kpiLabel}>총 주문</span>
                    <span style={styles.kpiValue}>{formatNumber(data.total_orders)}</span>
                  </div>
                </div>

                <div className="seller-kpi-col" style={styles.kpiCol}>
                  <div
                    style={styles.kpiBoxClickable}
                    onClick={() => go('/seller/kiosk/orders?status=TODAY')}
                  >
                    <span style={styles.kpiLabel}>오늘 주문</span>
                    <span style={styles.kpiValue}>{formatNumber(data.today_orders)}</span>
                  </div>
                </div>

                <div className="seller-kpi-col" style={styles.kpiCol}>
                  <div
                    style={styles.kpiBoxClickable}
                    onClick={() => go('/seller/kiosk/orders?status=NEW')}
                  >
                    <span style={styles.kpiLabel}>결제완료</span>
                    <span style={styles.kpiValue}>{formatNumber(data.payment_completed)}</span>
                  </div>
                </div>

                <div className="seller-kpi-col" style={styles.kpiCol}>
                  <div
                    style={styles.kpiBoxClickable}
                    onClick={() => go('/seller/kiosk/orders?status=DELIVERING')}
                  >
                    <span style={styles.kpiLabel}>배송중</span>
                    <span style={styles.kpiValue}>{formatNumber(data.delivery_in_progress)}</span>
                  </div>
                </div>

                <div className="seller-kpi-col" style={styles.kpiCol}>
                  <div
                    style={styles.kpiBoxClickable}
                    onClick={() => go('/seller/kiosk/orders?status=DONE')}
                  >
                    <span style={styles.kpiLabel}>배송완료</span>
                    <span style={styles.kpiValue}>{formatNumber(data.delivery_done)}</span>
                  </div>
                </div>

                <div className="seller-kpi-col" style={styles.kpiCol}>
                  <div
                    style={styles.kpiBoxClickable}
                    onClick={() => go('/seller/order/delivery-missing')}
                  >
                    <span style={{ ...styles.kpiLabel, color: '#dc3545' }}>미입력</span>
                    <span style={{ ...styles.kpiValue, color: '#dc3545' }}>
                      {formatNumber(data.delivery_missing)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div style={styles.card}>
              <h5
                style={styles.sectionTitleClickable}
                onClick={() => go('/seller/kiosk/orders?status=ALL')}
              >
                <span style={styles.sectionBar}></span>
                최근 주문 5건
              </h5>

              <div style={styles.tableResponsive}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.thCenter}>ID</th>
                      <th style={styles.thCenter}>주문자</th>
                      <th style={styles.thCenter}>결제금액</th>
                      <th style={styles.thCenter}>배송</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_orders.map((r) => (
                      <tr
                        key={r.id}
                        style={styles.clickableRow}
                        onClick={() => go(`/seller/kiosk/order/view?order_id=${encodeURIComponent(r.id)}`)}
                      >
                        <td style={styles.tdCenterMuted}>#{r.id}</td>
                        <td style={styles.tdCenterBold}>
                          {r.display_buyer_name || r.buyer_name || '-'}
                        </td>
                        <td style={styles.tdCenter}>
                          {formatNumber(r.show_total || r.result_price || 0)}원
                        </td>
                        <td style={styles.tdCenter}>
                          <span style={styles.badgeGrey}>
                            {r.delivery_status || '결제완료'}
                          </span>
                        </td>
                      </tr>
                    ))}

                    {data.recent_orders.length === 0 && (
                      <tr>
                        <td colSpan="4" style={styles.emptyTd}>
                          최근 주문이 없습니다.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={styles.card}>
              <h5
                style={styles.sectionTitleClickable}
                onClick={() => go('/seller/stock')}
              >
                <span style={styles.sectionBar}></span>
                재고 부족 상품 (10개 이하)
              </h5>

              <div style={styles.tableResponsive}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.thCenter}>이미지</th>
                      <th style={styles.thCenter}>상품명</th>
                      <th style={styles.thCenter}>재고</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.low_stock_products.map((p) => (
                      <tr
                        key={p.id}
                        style={styles.clickableRow}
                        onClick={() => go(`/seller/stock`)}
                      >
                        <td style={styles.tdCenter}>
                          <img
                            src={makeImageSrc(p.image_path)}
                            alt=""
                            style={styles.lowStockThumb}
                          />
                        </td>
                        <td style={styles.tdLeft}>
                          {truncateText(p.name || '', 40)}
                        </td>
                        <td style={styles.tdCenter}>
                          <span style={styles.badgeRed}>{p.stock}개</span>
                        </td>
                      </tr>
                    ))}

                    {data.low_stock_products.length === 0 && (
                      <tr>
                        <td colSpan="3" style={styles.emptyTd}>
                          재고 부족 상품이 없습니다.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div style={styles.column}>
            <div style={styles.card}>
              <h5
                style={styles.sectionTitleClickable}
                onClick={() => go('/seller/review')}
              >
                <span style={styles.sectionBar}></span>
                최근 리뷰
              </h5>

              <div style={styles.tableResponsive}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.thLeft}>상품/작성자</th>
                      <th style={styles.thCenter}>별점</th>
                      <th style={styles.thLeft}>내용</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_reviews.map((rev) => (
                      <tr
                        key={rev.id}
                        style={styles.clickableRow}
                        onClick={() => go('/seller/review')}
                      >
                        <td style={{ ...styles.tdLeft, width: '120px' }}>
                          <div style={styles.productNameEllipsis}>{rev.product_name}</div>
                          <div style={styles.smallMuted}>{rev.nickname}</div>
                        </td>
                        <td style={styles.ratingCell}>
                          {renderStars(rev.rating)}
                        </td>
                        <td style={styles.reviewTextCell}>
                          {truncateText(rev.content || '', 80)}
                        </td>
                      </tr>
                    ))}

                    {data.recent_reviews.length === 0 && (
                      <tr>
                        <td colSpan="3" style={styles.emptyTd}>
                          최근 리뷰가 없습니다.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={styles.card}>
              <h5
                style={styles.sectionTitleClickable}
                onClick={() => go('/seller/qna/list')}
              >
                <span style={styles.sectionBar}></span>
                최근 문의사항
              </h5>

              <div style={styles.tableResponsive}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.thLeft}>문의내용</th>
                      <th style={styles.thCenter}>상태</th>
                      <th style={styles.thCenter}>날짜</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_qna.map((q) => (
                      <tr
                        key={q.id}
                        style={styles.clickableRow}
                        onClick={() => go(`/seller/qna/view?id=${encodeURIComponent(q.id)}`)}
                      >
                        <td style={styles.tdLeft}>
                          <div style={styles.smallBoldMuted}>{q.nickname || ''}</div>
                          <div style={styles.qnaText}>
                            {q.is_secret ? '🔒 비밀글입니다.' : q.question}
                          </div>
                        </td>
                        <td style={styles.tdCenter}>
                          <span style={q.status === 'waiting' ? styles.badgeRed : styles.badgeGrey}>
                            {q.status === 'waiting' ? '미답변' : '완료'}
                          </span>
                        </td>
                        <td style={styles.qnaDateCell}>
                          {formatQnaDate(q.created_at)}
                        </td>
                      </tr>
                    ))}

                    {data.recent_qna.length === 0 && (
                      <tr>
                        <td colSpan="3" style={styles.emptyTd}>
                          최근 문의사항이 없습니다.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <br />
      </div>
    </SellerLayout>
  );
};

const responsiveCss = `
@media (max-width: 767px) {
  .seller-content-row {
    gap: 16px !important;
  }

  .seller-kpi-col {
    width: 50% !important;
  }
}
`;

const styles = {
  page: {
    fontFamily: "'Noto Sans KR', sans-serif",
    backgroundColor: '#f7f7f7',
    color: '#333',
    fontSize: '13px',
    minHeight: '100%',

    margin: '-16px',
    padding: '16px',
  },

  loadingWrap: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f4f6f9',
  },

  loadingText: {
    fontSize: '14px',
    color: '#666',
  },

  heroCard: {
    padding: '24px',
    marginBottom: '24px',
    background: '#fff',
    border: '0px solid #dee2e6',
    borderRadius: '0.5rem',
    boxShadow: '0 .125rem .25rem rgba(0,0,0,.075)',
  },

  heroTitle: {
    margin: 0,
    fontWeight: 700,
    letterSpacing: '-1px',
    fontSize: '24px',
  },

  heroDesc: {
    margin: '4px 0 0 0',
    color: '#6c757d',
    fontSize: '12px',
  },

  contentRow: {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap',
  },

  column: {
    flex: '1 1 480px',
  },

  card: {
    background: '#fff',
    border: '0px solid #e1e4e8',
    borderRadius: '8px',
    padding: '20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    marginBottom: '20px',
  },

  sectionTitle: {
    fontSize: '16px',
    fontWeight: 700,
    color: '#1a1a1a',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
  },

  sectionTitleClickable: {
    fontSize: '16px',
    fontWeight: 700,
    color: '#1a1a1a',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },

  sectionBar: {
    width: '3px',
    height: '14px',
    background: '#333',
    marginRight: '8px',
    borderRadius: '2px',
    display: 'inline-block',
  },

  kpiGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    margin: '-4px',
  },

  kpiCol: {
    width: '33.3333%',
    padding: '4px',
    boxSizing: 'border-box',
  },

  kpiBox: {
    background: '#fcfcfc',
    border: '1px solid #eee',
    borderRadius: '6px',
    padding: '12px 5px',
    textAlign: 'center',
  },

  kpiBoxClickable: {
    background: '#eee',
    border: '0px solid #eee',
    borderRadius: '6px',
    padding: '12px 5px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'background .15s ease, transform .15s ease',
  },

  kpiLabel: {
    fontSize: '11px',
    color: '#888',
    fontWeight: 500,
    marginBottom: '5px',
    display: 'block',
  },

  kpiValue: {
    fontSize: '16px',
    fontWeight: 700,
    color: '#222',
  },

  tableResponsive: {
    width: '100%',
    overflowX: 'auto',
  },

  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '12px',
    marginBottom: 0,
  },

  thCenter: {
    backgroundColor: '#f8f9fa',
    color: '#777',
    fontWeight: 600,
    borderBottom: '2px solid #eee',
    padding: '10px',
    fontSize: '11px',
    textTransform: 'uppercase',
    textAlign: 'center',
    whiteSpace: 'nowrap',
  },

  thLeft: {
    backgroundColor: '#f8f9fa',
    color: '#777',
    fontWeight: 600,
    borderBottom: '2px solid #eee',
    padding: '10px',
    fontSize: '11px',
    textTransform: 'uppercase',
    textAlign: 'left',
    whiteSpace: 'nowrap',
  },

  tdCenter: {
    padding: '12px 8px',
    borderBottom: '1px solid #f1f1f1',
    verticalAlign: 'middle',
    textAlign: 'center',
  },

  tdCenterMuted: {
    padding: '12px 8px',
    borderBottom: '1px solid #f1f1f1',
    verticalAlign: 'middle',
    textAlign: 'center',
    color: '#6c757d',
  },

  tdCenterBold: {
    padding: '12px 8px',
    borderBottom: '1px solid #f1f1f1',
    verticalAlign: 'middle',
    textAlign: 'center',
    fontWeight: 700,
  },

  tdLeft: {
    padding: '12px 8px',
    borderBottom: '1px solid #f1f1f1',
    verticalAlign: 'middle',
    textAlign: 'left',
  },

  emptyTd: {
    padding: '20px 8px',
    borderBottom: '1px solid #f1f1f1',
    textAlign: 'center',
    color: '#999',
  },

  clickableRow: {
    cursor: 'pointer',
  },

  lowStockThumb: {
    width: '32px',
    height: '32px',
    objectFit: 'cover',
    borderRadius: '4px',
    border: '1px solid #eee',
  },

  badgeRed: {
    display: 'inline-block',
    fontWeight: 500,
    fontSize: '10px',
    padding: '3px 6px',
    backgroundColor: '#fff1f0',
    color: '#cf1322',
    border: '1px solid #ffa39e',
    borderRadius: '999px',
  },

  badgeGrey: {
    display: 'inline-block',
    fontWeight: 500,
    fontSize: '10px',
    padding: '3px 6px',
    backgroundColor: '#f5f5f5',
    color: '#595959',
    border: '1px solid #d9d9d9',
    borderRadius: '999px',
  },

  productNameEllipsis: {
    fontWeight: 700,
    maxWidth: '100px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  smallMuted: {
    color: '#6c757d',
    fontSize: '12px',
  },

  smallBoldMuted: {
    fontSize: '12px',
    fontWeight: 700,
    color: '#6c757d',
  },

  ratingCell: {
    padding: '12px 8px',
    borderBottom: '1px solid #f1f1f1',
    verticalAlign: 'middle',
    textAlign: 'center',
    color: '#ffc107',
    whiteSpace: 'nowrap',
    letterSpacing: '-2px',
    fontSize: '10px',
  },

  reviewTextCell: {
    padding: '12px 8px',
    borderBottom: '1px solid #f1f1f1',
    verticalAlign: 'middle',
    textAlign: 'left',
    color: '#6c757d',
    fontSize: '12px',
  },

  qnaText: {
    maxWidth: '200px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  qnaDateCell: {
    padding: '12px 8px',
    borderBottom: '1px solid #f1f1f1',
    verticalAlign: 'middle',
    textAlign: 'center',
    color: '#6c757d',
    fontSize: '10px',
    whiteSpace: 'nowrap',
  },
};

export default SellerDashboard;